import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import { useAuth } from '../../contexts/AuthContext';
import dealService from '../../service/dealService.ts';
import offersService from '../../service/offersService.ts';
import dealAuditEventService from '../../service/DealAuditEventService.ts';
import { formatNumber } from '../../utils/formatUtils';
import {
  DataReceptionTab,
  ScreenerTab,
  PreApprovedTab,
  ContractTab,
  PreFundingTab,
  CollectionsTab
} from './tabs';
import FastMenu from './components/fast_menu';
import { appConfig } from '../../config/keycloak';

const DealDetails = () => {
  const { dealPublicId } = useParams();
  const navigate = useNavigate();
  const { getToken, getTokenParsed, user } = useAuth();
  const appUrl = appConfig.appUrl;
  
  // State management
  const [deal, setDeal] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Recuperar la tab guardada después de un save, o usar '1' por defecto
    const savedTab = localStorage.getItem('activeTabAfterSave');
    if (savedTab) {
      // Limpiar el localStorage después de usar el valor
      localStorage.removeItem('activeTabAfterSave');
      return savedTab;
    }
    return '1';
  });
  const [eventsPage, setEventsPage] = useState(0);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [underwriteLoading, setUnderwriteLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('danger');
  const [pendingPreApproval, setPendingPreApproval] = useState(false);
  const [originalOffers, setOriginalOffers] = useState([]);
  const [editedOffers, setEditedOffers] = useState([]);
  const [pendingOffersData, setPendingOffersData] = useState([]); // Store offers before saving to DB
  const [confirmPreApprovalLoading, setConfirmPreApprovalLoading] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectionLoading, setSelectionLoading] = useState(false);
  
  // Modal states for "Confirm on Behalf"
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [modalOffers, setModalOffers] = useState([]);
  const [modalEditingAmounts, setModalEditingAmounts] = useState({});
  const [modalSelectLoading, setModalSelectLoading] = useState({});
  const [generatingLink, setGeneratingLink] = useState(false);
  const [modalRenderKey, setModalRenderKey] = useState(0); // Force re-render key
  

  useEffect(() => {
    if (dealPublicId) {
      loadDealDetails();
      loadDealEvents();
    }
  }, [dealPublicId]);

  // Load offers and selection status only when entering the Pre-Approved tab
  useEffect(() => {
    if (activeTab === '3' && deal && (deal.status === 'OFFERS' || deal.status === 'PRE_APPROVED')) {
      // Only load if not already loaded
      if (offers.length === 0 && !offersLoading) {
        console.log('📊 Loading offers for Pre-Approved tab...');
        loadDealOffers(deal.privateId).then(() => {
          if (deal.privateId) {
            checkDealSelectionStatus(deal.privateId);
          }
        }).catch(error => {
          console.error('Error loading offers on tab change:', error);
        });
      }
    }
  }, [activeTab, deal]);

  const loadDealDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[DealDetails] Loading deal details for publicId:', dealPublicId);
      const token = getToken();
      const response = await dealService.getDealByPublicId(token, dealPublicId);
      const dealData = response.data;
      console.log('[DealDetails] Deal loaded:', {
        publicId: dealData.publicId,
        privateId: dealData.privateId,
        status: dealData.status,
        legalName: dealData.legalName
      });
      setDeal(dealData);
      
      // Determine the correct tab based on deal status
      const correctTab = determineCorrectTab(dealData.status);
      console.log('[DealDetails] Correct tab for status', dealData.status, 'is:', correctTab);
      if (correctTab && correctTab !== activeTab) {
        console.log('[DealDetails] Switching to tab:', correctTab);
        setActiveTab(correctTab);
      }
      
      // NOTE: Offers are now loaded lazily when the user enters the Pre-Approved tab
      // This is handled by the useEffect below that watches activeTab === '3'
      
    } catch (err) {
      console.error('Error loading deal details:', err);
      setError('Error loading deal details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to determine the correct tab based on deal status
  const determineCorrectTab = (status) => {
    switch (status) {
      case 'DATA_RECEPTION':
        return '1'; // Business Information
      case 'SCREENER':
        return '2'; // Screener
      case 'OFFERS':
      case 'PRE_APPROVED':
        return '3'; // Pre-Approved
      case 'CONTRACT':
        return '4'; // Contract
      case 'PRE_FUNDING':
        return '5'; // Prefunding
      case 'COLLECTIONS':
        return '6'; // Funding
      case 'DECLINED':
        // For declined deals, stay on the current tab or go to Business Information
        return activeTab || '1';
      default:
        return '1'; // Default to Business Information
    }
  };

  const loadDealEvents = async (page = 0, append = false) => {
    try {
      if (!append) setEventsLoading(true);
      
      const token = getToken();
      const response = await dealService.getDealEvents(token, dealPublicId, page, 5);
      const data = response.data;
      
      let eventsArray = [];
      let newHasMoreEvents = false;
      
      if (data && data.items && Array.isArray(data.items)) {
        eventsArray = data.items;
        newHasMoreEvents = data.hasNext || (data.items.length === 5);
      } else if (data && data.content && Array.isArray(data.content)) {
        eventsArray = data.content;
        newHasMoreEvents = !data.last;
      } else if (Array.isArray(data)) {
        eventsArray = data;
        newHasMoreEvents = data.length === 5;
      }
      
      if (append) {
        setEvents(prevEvents => [...prevEvents, ...eventsArray]);
      } else {
        setEvents(eventsArray);
      }
      
      setHasMoreEvents(newHasMoreEvents);
      setEventsPage(page);
      
    } catch (err) {
      console.error('Error loading deal events:', err);
      if (!append) {
        setEvents([]);
      }
    } finally {
      setEventsLoading(false);
    }
  };

  const loadMoreEvents = () => {
    loadDealEvents(eventsPage + 1, true);
  };

  const toggleTab = (tab) => {
    const allowedTabs = ['1', '2'];
    if (deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED' || pendingPreApproval) {
      allowedTabs.push('3');
    }

    if (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'DECLINED') {
      allowedTabs.push('4');
    }

    if (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'DECLINED') {
      allowedTabs.push('5');
    }

    if (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'PRE_FUNDING' && deal?.status !== 'DECLINED') {
      allowedTabs.push('6');
    }
    
    if (activeTab !== tab && allowedTabs.includes(tab)) {
      // If navigating away from Pre-Approved tab (tab '3') while pendingPreApproval is true
      // WITHOUT confirming, clear the pending state when going back to Screener tab
      if (activeTab === '3' && pendingPreApproval && tab === '2') {
        console.log('Navigating back to Screener tab, clearing pending pre-approval state...');
        setPendingPreApproval(false);
        setOriginalOffers([]);
        setEditedOffers([]);
        setPendingOffersData([]);
        setShowAlert(false);
      }
      
      setActiveTab(tab);
      
      if (tab === '3' && (deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED' || pendingPreApproval)) {
        if (!pendingPreApproval) {
          loadDealOffers();
          if (deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED') {
            if (deal.privateId) {
              checkDealSelectionStatus();
            } else {
              console.warn('Cannot check selection status: deal.privateId is missing');
            }
          }
        }
      }
    }
  };

  const formatCurrency = (amount) => {
    return formatNumber(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getEventSeverityColor = (severity) => {
    switch (severity) {
      case 'LOW': return 'success';
      case 'MEDIUM': return 'warning';
      case 'HIGH': return 'danger';
      case 'CRITICAL': return 'dark';
      default: return 'info';
    }
  };

  // Helper function to create audit events
  const createAuditEvent = async (token, eventName, description, referenceId = null) => {
    console.log(`[AUDIT] Starting audit event creation for: ${eventName}`);
    try {
      const tokenParsed = getTokenParsed();
      const userName = user?.preferred_username || tokenParsed?.preferred_username || 'Unknown User';
      
      const auditEventDto = {
        dealPrivateId: deal.privateId,
        dealPublicId: deal.publicId,
        origin: 'Deal Service',
        referenceId: referenceId || deal.publicId,
        event: eventName,
        description: description,
        date: new Date().toISOString(),
        userName: userName
      };
      
      const response = await dealAuditEventService.createDealAuditEvent(token, deal.privateId, auditEventDto);
      
      console.log(`[AUDIT] ✓ Audit event '${eventName}' saved successfully`);
      console.log(`[AUDIT] Response status:`, response.status);
      console.log(`[AUDIT] Response data:`, response.data);
    } catch (error) {
      console.error(`[AUDIT] ✗ Audit event '${eventName}' could not be saved`);
      console.error(`[AUDIT] Error message:`, error.message);
      console.error(`[AUDIT] Error response:`, error.response?.data);
      console.error(`[AUDIT] Error status:`, error.response?.status);
      console.error(`[AUDIT] Full error details:`, error);
    }
  };

  // Placeholder functions for complex logic (to be implemented)
  const handleUnderwrite = async () => {
    console.log('=== UNDERWRITE BUTTON CLICKED ===');
    try {
      setUnderwriteLoading(true);
      setShowAlert(false);
      const UNDERWRITING_STATUS_DECLINED = 'DECLINED';
      const token = getToken();
      console.log('Token obtained:', token ? 'Yes' : 'No');
      
      // Create the evaluation data variable
      const evaluationPayload = {
        application: {
          reference_id: deal.privateId,
          requested_amount: deal.requestedAmount,
          revenue: {
            total_subscribers: deal.totalSubscribers,
            total_monthly_revenue: deal.totalMonthlyRevenue
          }
        }
      };
      
      const evaluationResponse = await dealService.evaluateDeal(token, evaluationPayload);
      console.log("evaluationResponse: ", evaluationResponse);
      const evaluationData = evaluationResponse.data;
      console.log("evaluationData: ", evaluationData[0].status);
      if (evaluationData[0].status != null && evaluationData[0].status === UNDERWRITING_STATUS_DECLINED.toLowerCase()) {
        //
        var underwritingDeclinedResponse = {
          status: evaluationData[0].status,
          reason_code: evaluationData[0].reason_code ? evaluationData[0].reason_code : "INVALID_DEAL",
          reason_message: evaluationData[0].reason_message ?  evaluationData[0].reason_message : "The deal does not fulfill the acceptance requirements"
        };
        
        // Update deal status to DECLINED
        await dealService.updateDealStatus(token, deal.privateId, UNDERWRITING_STATUS_DECLINED);

        // Save declined reason
        await dealService.saveDeclinedReason(token, deal.privateId, underwritingDeclinedResponse.reason_message);
        
        // Send decline SMS notifications to owners
        await sendDeclineSms(token, underwritingDeclinedResponse.reason_message);
        
        // Send decline email notifications to owners
        await sendDeclineEmails(token, underwritingDeclinedResponse.reason_message);
        
        // Audit event: Underwrite - Declined
        await createAuditEvent(
          token,
          'Underwrite - Declined',
          `Deal has been declined. Reason: ${underwritingDeclinedResponse.reason_message}`,
          deal.publicId
        );
        
        // Message will be shown in the Underwriting section based on status
        
        // Refresh deal details to get updated status
        await loadDealDetails();
        
      } else {
        // Store offers data temporarily (NOT saving to database yet)
        console.log("Preparing offers data (not saving to DB yet)");
        const pendingOffers = [];
        if (evaluationResponse && evaluationData && evaluationData.length > 0) {
          console.log("evaluationData: ", evaluationData);
          for (const offer of evaluationData) {
             var underwritingApprovedResponse = {
              term_duration_months: offer.term_duration_months ? offer.term_duration_months : null, 
              gross_payback: offer.gross_payback ? offer.gross_payback : null,
              gross_advance_amount: offer.gross_advance_amount ? offer.gross_advance_amount : null,
              net_payback: offer.net_payback ? offer.net_payback : null,
              monthly_payment: offer.monthly_payment ? offer.monthly_payment : null,
              advance_amount: offer.advance_amount ? offer.advance_amount : null,
              factor: offer.factor ? offer.factor : null,
              processing_fee: offer.processing_fee ? offer.processing_fee : null,
              note: offer.note ? offer.note : null,
              flowId: offer.flowId ? offer.flowId : null,
              reference_id: offer.reference_id ? offer.reference_id : null
             };
            
            pendingOffers.push(underwritingApprovedResponse);
            console.log("Prepared offer: ", underwritingApprovedResponse);
          }
        }
        
        // Set pending pre-approval state
        setPendingPreApproval(true);
        
        // Store pending offers data (not saved to DB yet)
        setPendingOffersData(pendingOffers);
        
        // Store offers for editing (with temporary IDs for UI)
        const offersWithTempIds = pendingOffers.map((offer, index) => ({
          ...offer,
          id: `temp-${Date.now()}-${index}`, // Temporary ID for React keys
        }));
        setOriginalOffers(offersWithTempIds);
        setEditedOffers(offersWithTempIds.map(offer => ({ ...offer })));
        
        // Audit event: Underwrite - Approved
        const offersDescription = pendingOffers.map(o => 
          `Advance Amount: $${o.advance_amount}, Term: ${o.term_duration_months} months, Factor: ${o.factor}`
        ).join('; ');
        await createAuditEvent(
          token,
          'Underwrite - Approved',
          `Deal has been approved with ${pendingOffers.length} offer(s). Offers: ${offersDescription}`,
          deal.publicId
        );
        
        // Switch to Pre-Approved tab for editing
        setActiveTab('3');
        
        // Show success message
        setShowAlert(true);
        setAlertMessage('Pre-approval evaluation completed. Please review and confirm the offers below.');
        setAlertType('success');
      }
      
    } catch (err) {
      console.error('Error during underwriting:', err);
      setAlertMessage('Error processing underwriting: ' + err.message);
      setAlertType('danger');
      setShowAlert(true);
    } finally {
      setUnderwriteLoading(false);
    }
  };

  const loadDealOffers = async (privateId = null) => {
    try {
      setOffersLoading(true);
      const token = getToken();
      const dealPrivateId = privateId || deal?.privateId;
      
      if (!dealPrivateId) {
        console.warn('No privateId available to load offers');
        setOffers([]);
        return;
      }
      
      const response = await dealService.getDealOffers(token, dealPrivateId);
      
      console.log('Offers response:', response.data); // Debug log
      
      let offersArray = [];
      if (response.data && Array.isArray(response.data.content)) {
        offersArray = response.data.content;
      } else if (Array.isArray(response.data)) {
        offersArray = response.data;
      } else if (response.data && response.data.items && Array.isArray(response.data.items)) {
        offersArray = response.data.items;
      }
      
      console.log('Parsed offers array:', offersArray); // Debug log
      setOffers(offersArray);
    } catch (err) {
      console.error('Error loading offers:', err);
      setOffers([]);
    } finally {
      setOffersLoading(false);
    }
  };

  // Function to check deal selection status
  const checkDealSelectionStatus = async (privateId = null) => {
    try {
      setSelectionLoading(true);
      const token = getToken();
      
      if (!token) {
        console.warn('No token available for selection status check');
        setHasSelection(false);
        setSelectedOfferId(null);
        setSelectedOffer(null);
        return;
      }

      const dealPrivateId = privateId || deal?.privateId;
      
      if (!dealPrivateId) {
        console.warn('No privateId available for selection status check');
        setHasSelection(false);
        setSelectedOfferId(null);
        setSelectedOffer(null);
        return;
      }

      console.log('Checking selection status for deal:', dealPrivateId);
      console.log('Using token:', token ? 'Token present' : 'No token');
      
      const response = await dealService.getDealSelectionStatus(token, dealPrivateId);
      
      console.log('Selection status response:', response.data);
      
      if (response.data && response.data.hasSelection) {
        setHasSelection(true);
        setSelectedOfferId(response.data.selectedOfferId);
        
        // Load the selected offer details
        await loadSelectedOffer(response.data.selectedOfferId, dealPrivateId);
      } else {
        setHasSelection(false);
        setSelectedOfferId(null);
        setSelectedOffer(null);
      }
    } catch (err) {
      console.error('Error checking deal selection status:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          headers: err.config?.headers
        }
      });
      
      // If it's a 401 error, it might be an authentication issue
      if (err.response?.status === 401) {
        console.warn('Authentication error when checking selection status. This might be expected if the endpoint requires different permissions.');
      }
      
      // If it's a 404 error, the endpoint might not exist yet
      if (err.response?.status === 404) {
        console.warn('Selection status endpoint not found. This feature might not be available yet.');
      }
      
      // Set default values regardless of error
      setHasSelection(false);
      setSelectedOfferId(null);
      setSelectedOffer(null);
    } finally {
      setSelectionLoading(false);
    }
  };

  // Function to load selected offer details
  const loadSelectedOffer = async (offerId, privateId = null) => {
    try {
      const token = getToken();
      
      if (!token) {
        console.warn('No token available for loading selected offer');
        setSelectedOffer(null);
        return;
      }

      const dealPrivateId = privateId || deal?.privateId;
      
      if (!dealPrivateId) {
        console.warn('No privateId available for loading selected offer');
        setSelectedOffer(null);
        return;
      }

      if (!offerId) {
        console.warn('No offerId provided for loading selected offer');
        setSelectedOffer(null);
        return;
      }

      console.log('Loading selected offer:', offerId, 'for deal:', dealPrivateId);
      const response = await dealService.getDealOfferById(token, dealPrivateId, offerId);
      
      console.log('Selected offer response:', response.data);
      setSelectedOffer(response.data);
    } catch (err) {
      console.error('Error loading selected offer:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        offerId: offerId,
        dealPrivateId: privateId || deal?.privateId
      });
      
      // If it's a 401 error, log it but don't break the flow
      if (err.response?.status === 401) {
        console.warn('Authentication error when loading selected offer. This might be expected if the endpoint requires different permissions.');
      }
      
      // If it's a 404 error, the offer might not exist
      if (err.response?.status === 404) {
        console.warn('Selected offer not found. It might have been deleted or the ID is incorrect.');
      }
      
      setSelectedOffer(null);
    }
  };

  // Function to handle offer field changes
  const handleOfferChange = (offerId, field, value) => {
    setEditedOffers(prevOffers => 
      prevOffers.map(offer => 
        offer.id === offerId 
          ? { ...offer, [field]: parseFloat(value) || 0 }
          : offer
      )
    );
    
    // También actualizar pendingOffersData si existe
    setPendingOffersData(prevData => {
      if (prevData && prevData.length > 0) {
        // Encontrar el índice de la oferta en pendingOffersData (usar índice ya que tienen temp IDs)
        const tempIdMatch = offerId.toString().match(/temp-\d+-(\d+)/);
        if (tempIdMatch) {
          const index = parseInt(tempIdMatch[1]);
          return prevData.map((offer, idx) => 
            idx === index 
              ? { ...offer, [field]: parseFloat(value) || 0 }
              : offer
          );
        }
      }
      return prevData;
    });
  };

  // Function to confirm pre-approval
  const handleConfirmPreApproval = async () => {
    try {
      setConfirmPreApprovalLoading(true);
      const token = getToken();

      // Helper function to recalculate dependent fields
      const recalculateDependentFields = (offer) => {
        const grossAdvanceAmount = offer.gross_advance_amount || offer.grossAdvanceAmount || 0;
        const processingFee = offer.processing_fee || 0;
        const factor = offer.factor || 0;
        const termMonths = offer.term_duration_months || offer.term || 1;
        
        return {
          ...offer,
          advance_amount: grossAdvanceAmount - processingFee,
          net_payback: grossAdvanceAmount * factor,
          gross_payback: grossAdvanceAmount * factor,
          monthly_payment: (grossAdvanceAmount * factor) / termMonths
        };
      };

      // Step 1: Save pending offers to database if they haven't been saved yet
      if (pendingOffersData && pendingOffersData.length > 0) {
        console.log('Saving pending offers to database...');
        const createdOffers = [];
        
        // Recalcular campos dependientes antes de guardar
        const recalculatedPendingOffers = pendingOffersData.map(recalculateDependentFields);
        
        for (const offerData of recalculatedPendingOffers) {
          try {
            console.log('📝 Creating offer with calculated fields:', {
              advance_amount: offerData.advance_amount,
              gross_advance_amount: offerData.gross_advance_amount,
              net_payback: offerData.net_payback,
              gross_payback: offerData.gross_payback,
              monthly_payment: offerData.monthly_payment,
              factor: offerData.factor
            });
            
            const createdOffer = await dealService.createDealOffer(token, deal.privateId, offerData);
            createdOffers.push(createdOffer.data);
            console.log('Offer saved to database:', createdOffer.data);
          } catch (offerError) {
            console.error('Error saving offer:', offerError);
            throw new Error('Failed to save offers to database: ' + offerError.message);
          }
        }
        
        console.log('All pending offers saved successfully:', createdOffers.length);
        
        // Clear pending offers data after saving
        setPendingOffersData([]);
      } else {
        console.log('No pending offers to save, checking for modified offers...');
        
        // Step 2: Check which offers were modified and update them (for existing saved offers)
        // Recalcular campos dependientes para todas las ofertas editadas antes de compararlas
        const recalculatedEditedOffers = editedOffers.map(recalculateDependentFields);
        
        const modifiedOffers = recalculatedEditedOffers.filter(editedOffer => {
          const originalOffer = originalOffers.find(orig => orig.id === editedOffer.id);
          if (!originalOffer) return true; // New offer
          
          // Check if any field was modified
          return Object.keys(editedOffer).some(key => {
            if (key === 'id') return false; // Skip ID comparison
            return editedOffer[key] !== originalOffer[key];
          });
        });

        console.log('Modified offers (with recalculated fields):', modifiedOffers);

        // Update modified offers (only if they have real IDs, not temporary ones)
        for (const modifiedOffer of modifiedOffers) {
          const { id, ...offerData } = modifiedOffer;
          
          console.log('📝 Updating offer with calculated fields:', {
            id,
            advance_amount: offerData.advance_amount,
            gross_advance_amount: offerData.gross_advance_amount,
            net_payback: offerData.net_payback,
            gross_payback: offerData.gross_payback,
            monthly_payment: offerData.monthly_payment,
            factor: offerData.factor
          });
          
          // Skip temporary IDs
          if (id && !id.toString().startsWith('temp-')) {
            await dealService.updateDealOffer(token, deal.privateId, id, offerData);
          }
        }
      }

      // Step 3: Now proceed with status change and notifications
      await dealService.updateDealStatus(token, deal.privateId, 'OFFERS');
      await dealService.updateDealSubstatus(token, deal.privateId, 'OFFERS_SENT');

      // Send pre-approval SMS notifications to owners
      await sendPreApprovalSms(token);

      // Send pre-approval Email notifications to owners
      await sendPreApprovalEmails(token);

      // Audit event: Confirm Pre-Approval
      const confirmedOffersDescription = editedOffers.map(o => 
        `Offer ID: ${o.id}, Advance Amount: $${o.advance_amount}, Term: ${o.term_duration_months} months`
      ).join('; ');
      await createAuditEvent(
        token,
        'Confirm Pre-Approval',
        `Pre-approval confirmed and offers sent to client. ${editedOffers.length} offer(s) sent: ${confirmedOffersDescription}`,
        deal.publicId
      );

      // Reload offers so the table reflects the latest data after all requests
      await loadDealOffers();

      // Check for selected offer after confirmation
      if (deal && deal.privateId) {
        await checkDealSelectionStatus();
        } else {
        console.warn('Cannot check selection status after confirmation: deal.privateId is missing');
      }

      // Clear pending state
      setPendingPreApproval(false);
      setOriginalOffers([]);
      setEditedOffers([]);
      setPendingOffersData([]);

      // Refresh deal details to get updated status
      await loadDealDetails();
        
        // Show success message
        setShowAlert(true);
      setAlertMessage('Congratulations, your deal application has been pre-approved!');
        setAlertType('success');
        
    } catch (error) {
      console.error('Error confirming pre-approval:', error);
      setShowAlert(true);
      setAlertMessage('Error confirming pre-approval: ' + error.message);
      setAlertType('danger');
    } finally {
      setConfirmPreApprovalLoading(false);
    }
  };

  const handleCancelPreApproval = () => {
    // Reset pending state and restore original offers
    setPendingPreApproval(false);
    setEditedOffers([...originalOffers]);
    setOriginalOffers([]);
    setPendingOffersData([]); // Clear pending offers data
    
    // Show cancellation message
    setAlertMessage('Pre-approval cancelled. You can try underwriting again.');
    setAlertType('info');
    setShowAlert(true);
  };

  // Helper function to get full name from owner
  const getOwnerFullName = (owner) => {
    // Try different possible field names
    const firstName = owner.firstName || owner.first_name || '';
    const middleName = owner.middleName || owner.middle_name || '';
    const lastName = owner.lastName || owner.last_name || '';
    
    const fullName = [firstName, middleName, lastName]
      .filter(name => name && name.trim() !== '')
      .join(' ');
    
    return fullName || 'Unknown Owner';
  };

  // Helper function to find primary phone contact
  const getPrimaryPhoneContact = (owner) => {
    // Try different possible structures for contacts
    let contacts = owner.contacts || owner.contacs || []; // Note: "contacs" might be a typo in API
    
    if (!Array.isArray(contacts)) {
      return null;
    }

    return contacts.find(contact => 
      contact.isPrimary === true && 
      contact.deleted === false && 
      contact.type === 'PHONE'
    );
  };

  // Helper function to find primary email contact
  const getPrimaryEmailContact = (owner) => {
    // Try different possible structures for contacts
    let contacts = owner.contacts || owner.contacs || []; // Note: "contacs" might be a typo in API
    
    if (!Array.isArray(contacts)) {
      return null;
    }

    return contacts.find(contact => 
      contact.isPrimary === true && 
      contact.deleted === false && 
      contact.type === 'EMAIL'
    );
  };

  // Function to send decline SMS notifications
  const sendDeclineSms = async (token, reason_message) => {
    try {
      if (!deal.owners || !Array.isArray(deal.owners)) {
        console.log('No owners found in deal for decline SMS');
        return;
      }

      if (!deal.privateId) {
        console.error('No privateId found in deal for decline SMS');
        return;
      }

      console.log('Processing decline SMS notifications for owners:', deal.owners);

      // Get Decline SMS template
      const templateResponse = await dealService.getDeclineSmsTemplate(token);
      const templates = templateResponse.data;
      
      if (!templates || !Array.isArray(templates) || templates.length === 0) {
        console.error('No Decline SMS templates found');
        return;
      }

      const template = templates[0]; // Use the first template
      console.log('Original decline SMS template from server:', template);

      // Process each owner
      for (const owner of deal.owners) {
        console.log('Processing decline SMS for owner:', owner);
        
        const primaryPhone = getPrimaryPhoneContact(owner);
        
        if (!primaryPhone) {
          console.log(`No primary phone contact found for owner ${owner.id || owner.ownerId || 'unknown'}`);
          continue;
        }

        const ownerFullName = getOwnerFullName(owner);
        const ownerId = owner.id || owner.ownerId || owner.owner_id || `owner-${Date.now()}`;
        
        // Prepare template with replaced values
        const processedTemplate = {
          id: String(template.id).trim(),
          name: String(template.name).trim(),
          channel: String(template.channel).trim(),
          templateType: String(template.templateType).trim(),
          body: String(template.body).trim(),
          values: {
            reason: String(reason_message).trim(),
            account_id: String(dealPublicId).trim(),
            support_phone: "555-5555",
            user_name: String(ownerFullName).trim()
          }
        };

        // Generate unique request ID for decline SMS
        const declineSmsRequestId = `decline-sms-${Date.now()}-${dealPublicId}`;
        
        // Prepare communication payload
        const communicationPayload = {
          requestId: String(declineSmsRequestId).trim(),
          action: "DECLINE",
          dealId: deal.privateId,
          contacts: [
            {
              id: String(ownerId).trim(),
              name: String(ownerFullName).trim(),
              phone: String(primaryPhone.value).trim(),
              channels: ["SMS"]
            }
          ],
          templates: [processedTemplate]
        };

        console.log('Decline SMS Payload to send:', JSON.stringify(communicationPayload, null, 2));

        // Send Decline SMS
        try {
          await dealService.dispatchCommunication(token, communicationPayload);
          console.log(`Decline SMS sent successfully to owner ${ownerId} at ${primaryPhone.value}`);
        } catch (smsError) {
          console.error(`Failed to send decline SMS to owner ${ownerId}:`, smsError);
          console.error('Decline SMS Response data:', smsError.response?.data);
          console.error('Decline SMS Response status:', smsError.response?.status);
          console.error('Decline SMS Request config:', smsError.config);
        }
      }
    } catch (error) {
      console.error('Error in sendDeclineSms:', error);
    }
  };

  const sendDeclineEmails = async (token, reason_message) => {
    try {
      if (!deal.owners || !Array.isArray(deal.owners)) {
        console.log('No owners found in deal for decline emails');
        return;
      }

      if (!deal.privateId) {
        console.error('No privateId found in deal for decline emails');
        return;
      }

      console.log('Processing decline email notifications for owners:', deal.owners);

      // Get Decline Email template
      const templateResponse = await dealService.getDeclineEmailTemplate(token);
      const templates = templateResponse.data;
      
      if (!templates || !Array.isArray(templates) || templates.length === 0) {
        console.error('No Decline Email templates found');
        return;
      }

      const template = templates[0]; // Use the first template
      console.log('Original decline email template from server:', template);

      // Process each owner
      for (const owner of deal.owners) {
        console.log('Processing decline email for owner:', owner);
        
        const primaryEmail = getPrimaryEmailContact(owner);
        
        if (!primaryEmail) {
          console.log(`No primary email contact found for owner ${owner.id || owner.ownerId || 'unknown'}`);
          continue;
        }

        const ownerFullName = getOwnerFullName(owner);
        const ownerId = owner.id || owner.ownerId || owner.owner_id || `owner-${Date.now()}`;
        
        // Prepare template with replaced values
        const processedTemplate = {
          id: String(template.id).trim(),
          name: String(template.name).trim(),
          channel: String(template.channel).trim(),
          templateType: String(template.templateType).trim(),
          subject: String(template.subject).trim(),
          body: String(template.body).trim(),
          values: {
            reason: String(reason_message).trim(),
            company_logo_url: "https://example.com/logo.png", // Placeholder
            support_email: "support@nlf.com",
            account_id: String(dealPublicId).trim(),
            support_phone: "555-5555",
            user_name: String(ownerFullName).trim(),
            company_name: "NLF"
          }
        };

        // Generate unique request ID for decline email
        const declineEmailRequestId = `decline-email-${Date.now()}-${dealPublicId}`;

        // Prepare communication payload
        const communicationPayload = {
          requestId: String(declineEmailRequestId).trim(),
          action: "DECLINE",
          dealId: deal.privateId,
          contacts: [
            {
              id: String(ownerId).trim(),
              name: String(ownerFullName).trim(),
              email: String(primaryEmail.value).trim(),
              channels: ["EMAIL"]
            }
          ],
          templates: [processedTemplate]
        };

        console.log('Decline Email Payload to send:', JSON.stringify(communicationPayload, null, 2));

        // Send Decline Email
        try {
          await dealService.dispatchCommunication(token, communicationPayload);
          console.log(`Decline email sent successfully to owner ${ownerId} at ${primaryEmail.value}`);
        } catch (emailError) {
          console.error(`Failed to send decline email to owner ${ownerId}:`, emailError);
          console.error('Decline Email Response data:', emailError.response?.data);
          console.error('Decline Email Response status:', emailError.response?.status);
          // Continue with other owners even if one fails
        }
      }

    } catch (err) {
      console.error('Error sending decline emails:', err);
      // Don't throw error to avoid interrupting the main flow
    }
  };

  // Helper function to generate unique request ID
  const generateRequestId = () => {
    const timestamp = Date.now();
    return `pre-approval-sms-${timestamp}-${dealPublicId}`;
  };

  // Function to send pre-approval SMS notifications
  const sendPreApprovalSms = async (token) => {
    try {
      if (!deal.owners || !Array.isArray(deal.owners)) {
        console.log('No owners found in deal');
        console.log('Deal structure:', deal); // Debug log
        return;
      }

      if (!deal.privateId) {
        console.error('No privateId found in deal for pre-approval SMS');
        return;
      }

      console.log('Deal owners:', deal.owners); // Debug log

      // Get SMS template
      const templateResponse = await dealService.getPreApprovalSmsTemplate(token);
      const templates = templateResponse.data;
      
      if (!templates || !Array.isArray(templates) || templates.length === 0) {
        console.error('No SMS templates found');
        return;
      }

      const template = templates[0]; // Use the first template
      console.log('Original template from server:', template); // Debug log

      // Process each owner
      for (const owner of deal.owners) {
        console.log('Processing owner:', owner); // Debug log
        
        const primaryPhone = getPrimaryPhoneContact(owner);
        
        if (!primaryPhone) {
          console.log(`No primary phone contact found for owner ${owner.id || owner.ownerId || 'unknown'}`);
          continue;
        }

        const ownerFullName = getOwnerFullName(owner);
        console.log('Owner full name:', ownerFullName); // Debug log
        
        // Prepare template with only required fields (matching API structure)
        // Clean the template fields to avoid any whitespace or newline issues
        const processedTemplate = {
          id: String(template.id).trim(),
          name: String(template.name).trim(),
          channel: String(template.channel).trim(),
          templateType: String(template.templateType).trim(),
          body: String(template.body).trim(),
          values: {
            account_id: String(dealPublicId).trim(),
            support_phone: "555-5555",
            user_name: String(ownerFullName).trim()
          }
        };

        // Get owner ID with fallbacks
        const ownerId = owner.id || owner.ownerId || owner.owner_id || `owner-${Date.now()}`;

        // Prepare communication payload
        const communicationPayload = {
          requestId: String(generateRequestId()).trim(),
          action: "PRE_APPROVED",
          dealId: deal.privateId,
          contacts: [
            {
              id: String(ownerId).trim(),
              name: String(ownerFullName).trim(),
              phone: String(primaryPhone.value).trim(),
              channels: ["SMS"]
            }
          ],
          templates: [processedTemplate]
        };

        console.log('SMS Payload to send:', JSON.stringify(communicationPayload, null, 2)); // Debug log
        console.log('Token being used:', token ? 'Token present' : 'No token'); // Debug log

        // Send SMS
        try {
          await dealService.dispatchCommunication(token, communicationPayload);
          console.log(`SMS sent successfully to owner ${ownerId} at ${primaryPhone.value}`);
        } catch (smsError) {
          console.error(`Failed to send SMS to owner ${ownerId}:`, smsError);
          console.error('Response data:', smsError.response?.data);
          console.error('Response status:', smsError.response?.status);
          console.error('Request config:', smsError.config);
          // Continue with other owners even if one fails
        }
      }

    } catch (err) {
      console.error('Error sending pre-approval SMS:', err);
      // Don't throw error to avoid interrupting the main flow
    }
  };

  // Function to send pre-approval email notifications
  const sendPreApprovalEmails = async (token) => {
    try {
      if (!deal.owners || !Array.isArray(deal.owners)) {
        console.log('No owners found in deal for emails');
        return;
      }

      if (!deal.privateId) {
        console.error('No privateId found in deal for pre-approval emails');
        return;
      }

      console.log('Processing email notifications for owners:', deal.owners);

      // Get Email template
      const templateResponse = await dealService.getPreApprovalEmailTemplate(token);
      const templates = templateResponse.data;
      
      if (!templates || !Array.isArray(templates) || templates.length === 0) {
        console.error('No Email templates found');
        return;
      }

      const template = templates[0]; // Use the first template
      console.log('Original email template from server:', template);

      // Process each owner
      for (const owner of deal.owners) {
        console.log('Processing email for owner:', owner);
        
        const primaryEmail = getPrimaryEmailContact(owner);
        
        if (!primaryEmail) {
          console.log(`No primary email contact found for owner ${owner.id || owner.ownerId || 'unknown'}`);
          continue;
        }

        const ownerFullName = getOwnerFullName(owner);
        const ownerId = owner.id || owner.ownerId || owner.owner_id || `owner-${Date.now()}`;
        
        // Generate link for offers for this specific owner
        let offersUrl = '#'; // Default fallback
        try {
          const linkPayload = {
            ttlHours: 24,
            locale: "es-CR",
            reuseIfActive: false,
            recipient: {
              name: String(ownerFullName).trim(),
              email: String(primaryEmail.value).trim()
            },
            frontDomain: appUrl
          };

          console.log('Generating link for owner:', ownerId, linkPayload);
          const linkResponse = await dealService.generateDealLinkAndEmail(token, deal.privateId, linkPayload);
          
          if (linkResponse.data && linkResponse.data.url) {
            offersUrl = linkResponse.data.url;
            console.log('Generated offers URL for owner:', ownerId, offersUrl);
          }
        } catch (linkError) {
          console.error('Error generating offers link for owner:', ownerId, linkError);
          // Continue with default URL
        }
        
        // Prepare template with replaced values
        const processedTemplate = {
          id: String(template.id).trim(),
          name: String(template.name).trim(),
          channel: String(template.channel).trim(),
          templateType: String(template.templateType).trim(),
          subject: String(template.subject).trim(),
          body: String(template.body).trim(),
          values: {
            company_logo_url: "https://example.com/logo.png", // Placeholder
            user_email: String(primaryEmail.value).trim(),
            support_email: "support@nlf.com",
            account_id: String(dealPublicId).trim(),
            support_phone: "555-5555",
            user_name: String(ownerFullName).trim(),
            company_name: "NLF",
            offers_url: offersUrl
          }
        };

        // Generate unique request ID for email
        const emailRequestId = `pre-approval-email-${Date.now()}-${dealPublicId}`;

        // Prepare communication payload
        const communicationPayload = {
          requestId: String(emailRequestId).trim(),
          action: "PRE_APPROVED",
          dealId: deal.privateId,
          contacts: [
            {
              id: String(ownerId).trim(),
              name: String(ownerFullName).trim(),
              email: String(primaryEmail.value).trim(),
              channels: ["EMAIL"]
            }
          ],
          templates: [processedTemplate]
        };

        console.log('Email Payload to send:', JSON.stringify(communicationPayload, null, 2));

        // Send Email
        try {
          await dealService.dispatchCommunication(token, communicationPayload);
          console.log(`Email sent successfully to owner ${ownerId} at ${primaryEmail.value}`);
        } catch (emailError) {
          console.error(`Failed to send Email to owner ${ownerId}:`, emailError);
          console.error('Email Response data:', emailError.response?.data);
          console.error('Email Response status:', emailError.response?.status);
          // Continue with other owners even if one fails
        }
      }

    } catch (err) {
      console.error('Error sending pre-approval emails:', err);
      // Don't throw error to avoid interrupting the main flow
    }
  };

  // Function to handle "Confirm on Behalf" - Generate magic link and load offers
  const handleConfirmOnBehalf = async () => {
    console.log('🔵 handleConfirmOnBehalf called');
    console.log('Deal status:', deal?.status);
    console.log('Has selection:', hasSelection);
    console.log('Offers length:', offers?.length);
    console.log('Pending pre-approval:', pendingPreApproval);
    
    try {
      setGeneratingLink(true);
      setModalError(null);
      const token = getToken();

      console.log('🔵 Checking deal and owners...');
      console.log('Deal:', deal);
      console.log('Deal owners:', deal?.owners);
      
      if (!deal || !deal.owners || deal.owners.length === 0) {
        console.log('❌ No owners found for this deal');
        setModalError('No owners found for this deal.');
        return;
      }

      // Get the first owner's email for the magic link
      const primaryOwner = deal.owners[0];
      console.log('🔵 Primary owner:', primaryOwner);
      
      const primaryEmail = "getPrimaryEmailContact(primaryOwner)";
      console.log('🔵 Primary email:', primaryEmail);
      
      if (!primaryEmail) {
        console.log('❌ No primary email found');
        setModalError('No primary email found for the deal owner.');
        return;
      }

      const ownerFullName = getOwnerFullName(primaryOwner);
      console.log('🔵 Owner full name:', ownerFullName);

      // Generate magic link
      const linkPayload = {
        ttlHours: 24,
        locale: "es-CR",
        reuseIfActive: false, // Reuse if active for internal use
        recipient: {
          name: ownerFullName
        },
        frontDomain: appUrl
      };

      console.log('🔵 Generating magic link for internal use:', linkPayload);
      console.log('🔵 Deal privateId:', deal.privateId);
      console.log('🔵 Current offers in deal:', offers);
      
      console.log('🔵 Calling generateDealLinkAndEmail...');
      const linkResponse = await dealService.generateDealLinkAndEmail(token, deal.privateId, linkPayload);
      console.log('🔵 Link response:', linkResponse);
      
      if (linkResponse.data && linkResponse.data.url) {
        console.log('🔵 Magic link generated successfully:', linkResponse.data.url);
        const magicUrl = linkResponse.data.url;
        console.log('Generated magic URL:', magicUrl);
        console.log('Full link response:', linkResponse.data);
        
        // Extract token from the URL
        const tokenMatch = magicUrl.match(/#t=([^&]+)/);
        if (tokenMatch) {
          const magicToken = tokenMatch[1];
          console.log('Extracted magic token:', magicToken);
          
          // Audit event: Send Offer (Confirm on Behalf)
          const offersToSendDescription = offers.map(o => 
            `Offer ID: ${o.id}, Advance Amount: $${o.advance_amount}, Term: ${o.term_duration_months} months`
          ).join('; ');
          await createAuditEvent(
            token,
            'Send Offer - Confirm on Behalf',
            `Magic link generated for client to select offer. ${offers.length} offer(s) available: ${offersToSendDescription}. Link sent to: ${ownerFullName}`,
            deal.publicId
          );
          
          // Exchange the token and load offers directly in the modal
          await exchangeTokenAndLoadModalOffers(magicToken);
        } else {
          setModalError('Failed to extract token from magic link.');
        }
      } else {
        setModalError('Failed to generate magic link.');
      }
    } catch (error) {
      console.error('❌ Error in handleConfirmOnBehalf:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      setModalError('Failed to generate access link: ' + error.message);
    } finally {
      console.log('🔵 Setting generatingLink to false');
      setGeneratingLink(false);
    }
  };

  // Exchange magic token and load offers for the modal
  const exchangeTokenAndLoadModalOffers = async (magicToken) => {
    try {
      // Clear previous modal state completely
      setModalOffers([]);
      setModalEditingAmounts({});
      setModalSelectLoading({});
      if (window.modalSessionData) {
        window.modalSessionData = null;
      }
      
      setModalLoading(true);
      setModalError(null);

      // Step 1: Exchange magic token
      console.log('Exchanging magic token:', magicToken);
      const exchangeResponse = await offersService.exchangeMagicToken(magicToken);
      
      console.log('Exchange response:', exchangeResponse.data);
      
      if (exchangeResponse.data) {
        const { accessToken, sessionId } = exchangeResponse.data;
        console.log('Got accessToken and sessionId for modal');
        
        // Step 2: Load offers using the access token and session
        const offersResponse = await offersService.getDealsByLink(accessToken, sessionId);
        
        if (offersResponse.data && Array.isArray(offersResponse.data)) {
          console.log('Raw offers data from API:', offersResponse.data);
          
          // Instead of deduplicating by ID (which seems to have duplicates from backend),
          // let's deduplicate by unique combination of term + advance_amount + factor
          const uniqueOffersMap = new Map();
          var baseTimestamp = Date.now();
          var randomSuffix = Math.random().toString(36).substr(2, 9);
          offersResponse.data.forEach((offer, index) => {
            const uniqueKey = `modal-offer-${offer.id}-${baseTimestamp}-${index}-${randomSuffix}`;
            // const uniqueKey = `${offer.term_duration_months}-${offer.advance_amount}-${offer.factor}`;
            console.log(`Processing offer ${index}: uniqueKey=${uniqueKey}`, {
              id: offer.id,
              term_duration_months: offer.term_duration_months,
              advance_amount: offer.advance_amount,
              factor: offer.factor
            });
            if (!uniqueOffersMap.has(uniqueKey)) {
              uniqueOffersMap.set(uniqueKey, { ...offer, originalIndex: index });
              console.log(`Added unique offer with key: ${uniqueKey}`);
            } else {
              console.log(`Skipped duplicate offer with key: ${uniqueKey}`);
            }
          });

          console.log("Unique offers: ", uniqueOffersMap);
          
           const uniqueOffers = Array.from(uniqueOffersMap.values());
           console.log('Unique offers after deduplication by term+amount+factor:', uniqueOffers);
           
           // Create completely new objects with guaranteed unique IDs for frontend
           // Loop through the map to get both key and value
           const cleanedOffers = Array.from(uniqueOffersMap.entries()).map(([mapKey, offer], index) => {
             // Use the map key as the unique frontend ID
             var uniqueFrontendId = mapKey;
             console.log("unique key: ", uniqueFrontendId);
             const cleanedOffer = {
              // Use original ID for backend operations, but also store a unique frontend ID
              originalId: offer.id,
              id: uniqueFrontendId, // This ensures unique keys for React
              dealId: offer.dealId,
              term_duration_months: offer.term_duration_months || 0,
              gross_payback: offer.gross_payback || 0,
              gross_advance_amount: offer.gross_advance_amount || 0,
              net_payback: offer.net_payback || 0,
              monthly_payment: offer.monthly_payment || 0,
              advance_amount: offer.advance_amount || 0,
              factor: offer.factor || 0,
              processing_fee: offer.processing_fee || 0,
              note: offer.note || '',
              flowId: offer.flowId || '',
              createdAt: offer.createdAt || '',
              createdBy: offer.createdBy || '',
              updatedAt: offer.updatedAt || '',
              updatedBy: offer.updatedBy || '',
              _renderIndex: index
            };

            console.log(`Creating cleaned offer ${index} with ID: ${uniqueFrontendId}:`, {
              originalId: offer.id,
              frontendId: uniqueFrontendId,
              term: offer.term_duration_months,
              amount: offer.advance_amount,
              factor: offer.factor
            });
            return cleanedOffer;
          });
          
          console.log('Cleaned offers for modal:', cleanedOffers);
          console.log('All frontend IDs:', cleanedOffers.map(o => o.id));
          console.log('Number of offers to display:', cleanedOffers.length);
          
          // Double-check each offer has unique ID and different values
          cleanedOffers.forEach((offer, index) => {
            console.log(`Offer ${index + 1}:`, {
              id: offer.id,
              originalId: offer.originalId,
              term: offer.term,
              advanceAmount: offer.advanceAmount,
              factor: offer.factor
            });
          });
          
          // Verify all IDs are unique
          const ids = cleanedOffers.map(o => o.id);
          const uniqueIds = [...new Set(ids)];
          console.log('All IDs:', ids);
          console.log('Unique IDs:', uniqueIds);
          console.log('IDs are unique:', ids.length === uniqueIds.length);
          
          // Sort offers by term to ensure consistent ordering (6, 9, 12 months)
          const sortedOffers = [...cleanedOffers].sort((a, b) => a.term_duration_months - b.term_duration_months);
         
          // Set offers immediately without timeout - the issue might be with async state updates
          setModalOffers(sortedOffers);
          
          // Force re-render by updating the key
          setModalRenderKey(prev => prev + 1);
          
          // Verify state was set correctly (this will log in the next render cycle)
          setTimeout(() => {
            console.log('🔵 Verification - modalOffers after setState:', modalOffers.length, 'offers');
            console.log('🔵 Verification - modalOffers IDs:', modalOffers.map(o => o.id));
          }, 100);
          
          // Initialize editing amounts using the sorted offers
          const initialAmounts = {};
          cleanedOffers.forEach(offer => {
            if (offer.advance_amount) {
              initialAmounts[offer.id] = offer.advance_amount;
              console.log(`Set initial amount for offer ${offer.id}: ${offer.advance_amount}`);
            }
          });
          setModalEditingAmounts(initialAmounts);
          console.log('Initial editing amounts:', initialAmounts);
          
          // Store session data for offer selection, including dealId for contract communication
          const dealPrivateId = deal?.privateId; // Get dealPrivateId from current deal
          window.modalSessionData = { accessToken, sessionId, dealPrivateId };
          
          // Show the modal
          setShowOffersModal(true);
        } else {
          setModalError('Invalid response from server when loading offers.');
        }
      }
    } catch (error) {
      console.error('Error exchanging token and loading offers:', error);
      setModalError('Failed to load offers: ' + error.message);
    } finally {
      setModalLoading(false);
    }
  };

  // Function to send contract communication after offer selection
  const sendContractCommunication = async (token, dealPrivateId) => {
    try {
      console.log('📄 Getting funding document template...');
      
      // Step 1: Get the funding document template
      const templateResponse = await dealService.getFundingDocumentTemplate(token);
      const templateArray = templateResponse.data;
      
      if (!templateArray || !Array.isArray(templateArray) || templateArray.length === 0) {
        throw new Error('FUNDING_DOCUMENT template not found');
      }
      
      const template = templateArray[0]; // Get the first template from the array
      console.log('📄 Template obtained:', template);
      
      // Step 2: Get deal data by private ID
      console.log('🔍 Getting deal data by private ID:', dealPrivateId);
      const dealResponse = await dealService.getDealByPrivateId(token, dealPrivateId);
      const dealData = dealResponse.data;
      
      // Step 3: Get selection status
      console.log('🔍 Getting selection status...');
      const selectionResponse = await dealService.getDealSelectionStatus(token, dealPrivateId);
      const selectionData = selectionResponse.data;
      
      console.log('📊 Deal data:', dealData);
      console.log('📊 Selection data:', selectionData);
      
      // Step 4: Get selected offer details if available
      let selectedOfferData = null;
      if (selectionData.selectedOfferId) {
        try {
          const offerResponse = await dealService.getDealOfferById(token, dealPrivateId, selectionData.selectedOfferId);
          selectedOfferData = offerResponse.data;
          console.log('📊 Selected offer data:', selectedOfferData);
        } catch (offerError) {
          console.warn('Could not get selected offer details:', offerError);
        }
      }
      
      // Step 5: Get owner details for template values
      const primaryOwner = dealData.owners && dealData.owners.length > 0 ? dealData.owners[0] : null;
      const secondaryOwner = dealData.owners && dealData.owners.length > 1 ? dealData.owners[1] : null;
      
      const primaryEmail = primaryOwner ? getPrimaryEmailContact(primaryOwner) : null;
      const primaryPhone = primaryOwner ? getPrimaryPhoneContact(primaryOwner) : null;
      
      // Step 6: Get address details from primary owner's addresses array
      const physicalAddress = primaryOwner && primaryOwner.addresses && Array.isArray(primaryOwner.addresses) 
        ? primaryOwner.addresses.find(addr => addr.addressType === 'PHYSICAL' && !addr.deleted)
        : null;
      
      // Step 7: Process the template with all available data
      const processedTemplate = {
        id: String(template.id || '').trim(),
        name: String(template.name || '').trim(),
        channel: String(template.channel || '').trim(),
        templateType: String(template.templateType || '').trim(),
        subject: String(template.subject || '').trim(),
        body: String(template.body || '').trim(),
        values: {
          // Deal basic info
          referenceId: String(dealData.publicId || dealPublicId || '').trim(),
          prePaymentAddendum: false,
          legal_name: String(dealData.legalName || '').trim(),
          dba_name: String(dealData.dbaName || '').trim(),
          business_structure: String(dealData.businessStructure || '').trim(),
          state_of_incorporation: String(dealData.stateOfInc || '').trim(),
          
          // Owner info
          first_name: primaryOwner ? String(primaryOwner.firstName || primaryOwner.first_name || '').trim() : '',
          last_name: primaryOwner ? String(primaryOwner.lastName || primaryOwner.last_name || '').trim() : '',
          owner2_full_name: secondaryOwner ? String(getOwnerFullName(secondaryOwner) || '').trim() : '',
          
          // Contact info
          email_1: primaryEmail ? String(primaryEmail.value || '').trim() : '',
          mobile_phone: primaryPhone ? String(primaryPhone.value || '').trim() : '',
          
          // Address info from addresses array
          address: physicalAddress ? String(physicalAddress.street1 || '').trim() : '',
          city: physicalAddress ? String(physicalAddress.city || '').trim() : '',
          state: physicalAddress ? String(physicalAddress.state || '').trim() : '',
          zip: physicalAddress ? String(physicalAddress.zipCode || '').trim() : '',
          
          // Financial info from selected offer
          advance_amount: selectedOfferData ? String(selectedOfferData.advance_amount || 0).trim() : '0',
          payback_amount: selectedOfferData ? String(selectedOfferData.gross_payback || selectedOfferData.payback || 0).trim() : '0',
          net_funded_amount: selectedOfferData ? String(selectedOfferData.net_payback || selectedOfferData.advance_amount || 0).trim() : '0',
          daily_ach_amount: selectedOfferData ? String(selectedOfferData.monthly_payment || 0).trim() : '0',
          origination_fee: selectedOfferData ? String(selectedOfferData.processing_fee || 0).trim() : '0',
          gross_percentage: selectedOfferData ? String(selectedOfferData.factor || 0).trim() : '0',
          
          // Additional fields that might be needed
          sum_of_deductions: '0',
          combined_balance: '0',
          third_party_amount: '0'
        }
      };
      
      console.log('📄 Processed template:', processedTemplate);
      
      // Step 8: Prepare contacts from deal owners
      const contacts = [];
      
      if (dealData.owners && Array.isArray(dealData.owners)) {
        for (const owner of dealData.owners) {
          const ownerEmail = getPrimaryEmailContact(owner);
          
          if (ownerEmail) {
            const ownerFullName = getOwnerFullName(owner);
            const ownerId = owner.id || owner.ownerId || owner.owner_id || `owner-${Date.now()}`;
            
            contacts.push({
              id: String(ownerId).trim(),
              name: String(ownerFullName).trim(),
              email: String(ownerEmail.value).trim(),
              channels: ["EMAIL"]
            });
          }
        }
      }
      
      if (contacts.length === 0) {
        throw new Error('No valid email contacts found for contract communication');
      }
      
      // Step 9: Generate contract request ID
      const contractRequestId = `contract-${Date.now()}-${dealPublicId}`;
      
      // Step 10: Prepare and send communication payload
      const communicationPayload = {
        requestId: String(contractRequestId).trim(),
        action: "CONTRACT",
        dealId: dealPrivateId,
        contacts: contacts,
        templates: [processedTemplate]
      };
      
      console.log('📧 Contract communication payload:', JSON.stringify(communicationPayload, null, 2));
      
      // Step 11: Send the communication
      await dealService.dispatchCommunication(token, communicationPayload);
      
      console.log('✅ Contract communication sent successfully');
      
    } catch (error) {
      console.error('❌ Error in contract communication:', error);
      throw error;
    }
  };

  // Handle modal offer selection
  const handleModalSelectOffer = async (offerId) => {
    // Check if any offer is already being selected
    if (Object.values(modalSelectLoading).some(loading => loading)) {
      console.warn('⚠️ An offer is already being selected, ignoring this click');
      return;
    }
    
    if (!window.modalSessionData) {
      setModalError('Session expired. Please try again.');
      return;
    }

    const { accessToken, sessionId, dealPrivateId } = window.modalSessionData;
    
    // Find the offer to get the default amount
    const offer = modalOffers.find(o => o.id === offerId);
    if (!offer) {
      setModalError('Offer not found.');
      return;
    }
    
    // Get amount from editing state or use default offer amount
    const amount = modalEditingAmounts[offerId] || offer.advance_amount;
    
    if (!amount || amount <= 0) {
      setModalError('Please specify a valid amount for the selected offer.');
      return;
    }

    console.log(`Selecting offer ${offerId} with amount ${amount} on behalf of client`);
    console.log('Modal editing amounts:', modalEditingAmounts);
    console.log('Offer advance_amount:', offer.advance_amount);
    console.log('Final amount being sent:', amount);
    
    // Set loading state IMMEDIATELY before any async operations
    setModalSelectLoading(prev => ({ ...prev, [offerId]: true }));
    setModalError(null);

    try {
      // Find the original ID for the backend call
      const offer = modalOffers.find(o => o.id === offerId);
      const backendOfferId = offer ? offer.originalId : offerId;
      
      console.log(`Using backend offer ID: ${backendOfferId} for frontend ID: ${offerId}`);
      const response = await offersService.selectOffer(backendOfferId, amount, accessToken, sessionId);
      
      if (response.data) {
        console.log('Offer selected successfully on behalf of client:', response.data);
        
        // Don't close modal - let PreApprovedTab handle the blank check flow
        // The modal will be closed by PreApprovedTab after blank check upload/skip
        
        // Clean up loading state but keep modal open
        setModalSelectLoading(prev => ({ ...prev, [offerId]: false }));
        setModalError(null);
        
        // Update deal status to CONTRACT after successful offer selection
        console.log('🔄 Updating deal status to CONTRACT after offer selection...');
        const token = getToken();
        if (token && deal?.privateId) {
          try {
            await dealService.updateDealStatus(token, deal.privateId, 'CONTRACT');
            await dealService.updateDealSubstatus(token, deal.privateId, 'CONTRACT_SENT');
            console.log('✅ Deal status updated to CONTRACT successfully');
          } catch (statusError) {
            console.error('❌ Error updating deal status to CONTRACT:', statusError);
            // Don't fail the whole process if status update fails
          }
        }

        // Send contract communication after successful offer selection
        if (token && dealPrivateId) {
          try {
            console.log('🔄 Starting contract communication flow...');
            await sendContractCommunication(token, dealPrivateId);
            console.log('✅ Contract communication sent successfully');
          } catch (contractError) {
            console.error('❌ Error sending contract communication:', contractError);
            // Don't fail the whole process if contract communication fails
          }
        }
        
        // Show success message
        setShowAlert(true);
        setAlertMessage('Offer selected successfully on behalf of the client! Deal status updated to CONTRACT and contract communication sent.');
        setAlertType('success');
        
        // Refresh the deal details and check selection status
        await loadDealDetails();
        await checkDealSelectionStatus();
      }
    } catch (error) {
      console.error('Error selecting offer on behalf:', error);
      if (error.response && error.response.data && error.response.data.message) {
        setModalError(`Failed to select offer: ${error.response.data.message}`);
      } else {
        setModalError('Failed to select offer. Please try again.');
      }
    } finally {
      setModalSelectLoading(prev => ({ ...prev, [offerId]: false }));
    }
  };

  // Handle modal amount adjustment
  const handleModalAmountChange = (offerId, newAmount) => {
    setModalEditingAmounts(prev => ({
      ...prev,
      [offerId]: newAmount
    }));
  };

  // Get amount limits for modal offers (same logic as OffersSelect)
  const getModalAmountLimits = (currentOffer, allOffers) => {
    const sortedOffers = allOffers.sort((a, b) => a.advance_amount - b.advance_amount);
    const currentIndex = sortedOffers.findIndex(offer => offer.id === currentOffer.id);
    
    const minAmount = currentOffer.advance_amount;
    
    let maxAmount;
    if (currentIndex === sortedOffers.length - 1) {
      maxAmount = currentOffer.advance_amount;
    } else {
      maxAmount = sortedOffers[currentIndex + 1].advance_amount - 500;
    }
    
    return { minAmount, maxAmount };
  };

  // Validate modal amount
  const isValidModalAmount = (offer, amount) => {
    const { minAmount, maxAmount } = getModalAmountLimits(offer, modalOffers);
    return amount >= minAmount && amount <= maxAmount && amount % 500 === 0;
  };

  // Adjust modal amount
  const adjustModalAmount = (offerId, direction) => {
    const offer = modalOffers.find(o => o.id === offerId);
    if (!offer) return;

    const currentAmount = modalEditingAmounts[offerId] || offer.advance_amount;
    const newAmount = direction === 'increment' ? currentAmount + 500 : currentAmount - 500;
    
    if (isValidModalAmount(offer, newAmount)) {
      handleModalAmountChange(offerId, newAmount);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowOffersModal(false);
    setModalError(null);
    setModalOffers([]);
    setModalEditingAmounts({});
    setModalSelectLoading({});
    if (window.modalSessionData) {
      window.modalSessionData = null;
    }
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-2">Loading deal details...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
            <Alert color="danger">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <hr />
          <Button color="danger" onClick={() => navigate('/deals')}>
            Back to Deals
            </Button>
        </Alert>
      </Container>
    );
  }

  if (!deal) {
    return (
      <Container className="mt-5">
            <Alert color="warning">
          <h4 className="alert-heading">Deal Not Found</h4>
          <p>The requested deal could not be found.</p>
          <hr />
          <Button color="warning" onClick={() => navigate('/deals')}>
            Back to Deals
            </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Container className="mt-4 h-auto" fluid>
        {/* Header with Back Button and Fast Menu */}
        <Row className="mb-3">
          <Col>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <Button
                  color="light"
                  size="sm"
                  onClick={() => navigate('/deals')}
                  className="mr-3"
                  style={{ border: '1px solid #dee2e6' }}
                >
                  Go Back
                </Button>
                <div>
                {deal && (
                  <p className="text-black mb-0 mt-1">
                    <span className="font-weight-bold">{deal.publicId}</span>
                    {deal.legalName && (
                      <>
                        <span className="mx-2">•</span>
                        <span>{deal.legalName}</span>
                      </>
                    )}
                  </p>
                )}
              </div>
              </div>
              {/* Fast Menu in the top right */}
              <FastMenu deal={deal} />
            </div>
          </Col>
        </Row>

        {/* Alert Messages */}
        {showAlert && (
          <Row className="mb-3">
            <Col>
              <Alert 
                color={alertType} 
                isOpen={showAlert} 
                toggle={() => setShowAlert(false)}
                className={alertType === 'success' ? 'alert-success' : ''}
              >
                <i className={`fas fa-${alertType === 'success' ? 'check-circle' : 'exclamation-triangle'} mr-2`} />
                {alertMessage}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Blank Check Warning Banner - Only show if hasBlankCheck is false and deal is in CONTRACT status or beyond */}
        {deal && deal.hasBlankCheck === false && (deal.status === 'CONTRACT' || deal.status === 'PRE_FUNDING' || deal.status === 'COLLECTIONS') && (
          <Row className="mb-3">
            <Col>
              <Alert color="warning" className="d-flex align-items-center">
                <i className="ni ni-bell-55 mr-2" style={{ fontSize: '1.5rem' }} />
                <strong>This deal hasn't uploaded a blank check yet.</strong>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Navigation Tabs */}
        <Row className="mb-4">
          <Col>
            <Nav tabs className="nav-fill flex-column flex-md-row" id="tabs-icons-text" role="tablist">
              {/* Business Information - Enabled with green color */}
              <NavItem>
                <NavLink
                  aria-selected={activeTab === '1'}
                  className={`mb-sm-3 mb-md-0 ${activeTab === '1' ? 'active' : ''}`}
                  onClick={() => toggleTab('1')}
                  href="#"
                  role="tab"
                  style={{ 
                    color: activeTab === '1' ? '#fff' : '#28a745',
                    backgroundColor: activeTab === '1' ? '#28a745' : 'transparent',
                    borderColor: '#28a745',
                    cursor: 'pointer'
                  }}
                >
                  <i className="ni ni-briefcase-24 mr-2"></i>
                  Business Information
                </NavLink>
              </NavItem>
              
              {/* Screener - Enabled with green color */}
              <NavItem>
                <NavLink
                  aria-selected={activeTab === '2'}
                  className={`mb-sm-3 mb-md-0 ${activeTab === '2' ? 'active' : ''}`}
                  onClick={() => toggleTab('2')}
                  href="#"
                  role="tab"
                  style={{ 
                    color: activeTab === '2' ? '#fff' : '#28a745',
                    backgroundColor: activeTab === '2' ? '#28a745' : 'transparent',
                    borderColor: '#28a745',
                    cursor: 'pointer'
                  }}
                >
                  <i className="ni ni-check-bold mr-2"></i>
                  Screener
                </NavLink>
              </NavItem>
              
              {/* Pre-Approved - Active when status is NOT SCREENER or DECLINED */}
              <NavItem>
                <NavLink
                  aria-selected={activeTab === '3'}
                  className={`mb-sm-3 mb-md-0 ${activeTab === '3' ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const isEnabled = deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED' || pendingPreApproval;
                    if (isEnabled) {
                      toggleTab('3');
                    }
                  }}
                  href="#"
                  role="tab"
                  style={{ 
                    color: (deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED' || pendingPreApproval) ? (activeTab === '3' ? '#fff' : '#28a745') : '#6c757d',
                    backgroundColor: (deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED' || pendingPreApproval) ? (activeTab === '3' ? '#28a745' : 'transparent') : '#f8f9fa',
                    borderColor: (deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED' || pendingPreApproval) ? '#28a745' : '#dee2e6',
                    cursor: (deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED' || pendingPreApproval) ? 'pointer' : 'not-allowed',
                    opacity: (deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED' || pendingPreApproval) ? 1 : 0.6,
                    pointerEvents: (deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED' || pendingPreApproval) ? 'auto' : 'none'
                  }}
                >
                  <i className="ni ni-single-copy-04 mr-2"></i>
                  Offers
                </NavLink>
              </NavItem>
              
              {/* Contract - Active when status is NOT SCREENER, OFFERS, or DECLINED */}
              <NavItem>
                <NavLink
                  aria-selected={activeTab === '4'}
                  className={`mb-sm-3 mb-md-0 ${activeTab === '4' ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const isEnabled = deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'DECLINED';
                    if (isEnabled) {
                      toggleTab('4');
                    }
                  }}
                  href="#"
                  role="tab"
                  style={{ 
                    color: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'DECLINED') ? (activeTab === '4' ? '#fff' : '#28a745') : '#6c757d',
                    backgroundColor: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'DECLINED') ? (activeTab === '4' ? '#28a745' : 'transparent') : '#f8f9fa',
                    borderColor: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'DECLINED') ? '#28a745' : '#dee2e6',
                    cursor: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'DECLINED') ? 'pointer' : 'not-allowed',
                    opacity: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'DECLINED') ? 1 : 0.6,
                    pointerEvents: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'DECLINED') ? 'auto' : 'none'
                  }}
                >
                  <i className="ni ni-paper-diploma mr-2"></i>
                  Contract
                </NavLink>
              </NavItem>
              
              {/* Prefunding - Active when status is NOT SCREENER, PRE_APPROVED, CONTRACT, or DECLINED */}
              <NavItem>
                <NavLink
                  aria-selected={activeTab === '5'}
                  className={`mb-sm-3 mb-md-0 ${activeTab === '5' ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const isEnabled = deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'DECLINED';
                    if (isEnabled) {
                      toggleTab('5');
                    }
                  }}
                  href="#"
                  role="tab"
                  style={{ 
                    color: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'DECLINED') ? (activeTab === '5' ? '#fff' : '#28a745') : '#6c757d',
                    backgroundColor: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'DECLINED') ? (activeTab === '5' ? '#28a745' : 'transparent') : '#f8f9fa',
                    borderColor: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'DECLINED') ? '#28a745' : '#dee2e6',
                    cursor: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'DECLINED') ? 'pointer' : 'not-allowed',
                    opacity: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'DECLINED') ? 1 : 0.6,
                    pointerEvents: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'DECLINED') ? 'auto' : 'none'
                  }}
                >
                  <i className="ni ni-time-alarm mr-2"></i>
                  Prefunding
                </NavLink>
              </NavItem>
              
              {/* Funding - Active when status is NOT SCREENER, PRE_APPROVED, CONTRACT, PRE_FUNDING, or DECLINED */}
              <NavItem>
                <NavLink
                  aria-selected={activeTab === '6'}
                  className={`mb-sm-3 mb-md-0 ${activeTab === '6' ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const isEnabled = deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'PRE_FUNDING' && deal?.status !== 'DECLINED';
                    if (isEnabled) {
                      toggleTab('6');
                    }
                  }}
                  href="#"
                  role="tab"
                  style={{ 
                    color: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'PRE_FUNDING' && deal?.status !== 'DECLINED') ? (activeTab === '6' ? '#fff' : '#28a745') : '#6c757d',
                    backgroundColor: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'PRE_FUNDING' && deal?.status !== 'DECLINED') ? (activeTab === '6' ? '#28a745' : 'transparent') : '#f8f9fa',
                    borderColor: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'PRE_FUNDING' && deal?.status !== 'DECLINED') ? '#28a745' : '#dee2e6',
                    cursor: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'PRE_FUNDING' && deal?.status !== 'DECLINED') ? 'pointer' : 'not-allowed',
                    opacity: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'PRE_FUNDING' && deal?.status !== 'DECLINED') ? 1 : 0.6,
                    pointerEvents: (deal?.status !== 'SCREENER' && deal?.status !== 'OFFERS' && deal?.status !== 'CONTRACT' && deal?.status !== 'PRE_FUNDING' && deal?.status !== 'DECLINED') ? 'auto' : 'none'
                  }}
                >
                  <i className="ni ni-money-coins mr-2"></i>
                  Collections
                </NavLink>
              </NavItem>
            </Nav>
          </Col>
        </Row>

        {/* Tab Content */}
        <TabContent activeTab={activeTab}>
          {/* Tab 1: Business Information */}
          <TabPane tabId="1">
            {activeTab === '1' && (
            <DataReceptionTab 
              deal={deal}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
            />
            )}
          </TabPane>

          {/* Tab 2: Screener */}
          <TabPane tabId="2">
            {activeTab === '2' && (
            <ScreenerTab 
              deal={deal}
              dealPublicId={dealPublicId}
              handleUnderwrite={handleUnderwrite}
              underwriteLoading={underwriteLoading}
            />
            )}
          </TabPane>

          {/* Tab 3: Pre-Approved */}
          <TabPane tabId="3">
            {activeTab === '3' && (
            <PreApprovedTab 
              deal={deal}
              pendingPreApproval={pendingPreApproval}
              hasSelection={hasSelection}
              selectedOffer={selectedOffer}
              offers={offers}
              editedOffers={editedOffers}
              offersLoading={offersLoading}
              selectionLoading={selectionLoading}
              confirmPreApprovalLoading={confirmPreApprovalLoading}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              handleOfferChange={handleOfferChange}
              handleConfirmPreApproval={handleConfirmPreApproval}
              handleCancelPreApproval={handleCancelPreApproval}
              // Props for Confirm on Behalf functionality
              handleConfirmOnBehalf={handleConfirmOnBehalf}
              generatingLink={generatingLink}
              modalLoading={modalLoading}
              showOffersModal={showOffersModal}
              modalError={modalError}
              modalOffers={modalOffers}
              modalEditingAmounts={modalEditingAmounts}
              modalSelectLoading={modalSelectLoading}
                setModalSelectLoading={setModalSelectLoading}
              handleCloseModal={handleCloseModal}
              handleModalSelectOffer={handleModalSelectOffer}
              adjustModalAmount={adjustModalAmount}
              isValidModalAmount={isValidModalAmount}
              handleModalAmountChange={handleModalAmountChange}
              // Functions for editing offers flow
              loadDealOffers={loadDealOffers}
              sendPreApprovalSms={sendPreApprovalSms}
              sendPreApprovalEmails={sendPreApprovalEmails}
                // Blank check status
                hasBlankCheck={deal?.hasBlankCheck}
            />
            )}
          </TabPane>

          {/* Tab 4: Contract */}
          <TabPane tabId="4">
            {activeTab === '4' && (
            <ContractTab deal={deal} />
            )}
          </TabPane>

          {/* Tab 5: Prefunding */}
          <TabPane tabId="5">
            {activeTab === '5' && (
            <PreFundingTab deal={deal} />
            )}
          </TabPane>

          {/* Tab 6: Funding */}
          <TabPane tabId="6">
            {activeTab === '6' && (
            <CollectionsTab deal={deal} />
            )}
          </TabPane>
        </TabContent>
      </Container>
    </>
  );
};

export default DealDetails;
